import {createReduxStore, dispatch, register, select, subscribe, useDispatch, useSelect} from '@wordpress/data';

import {apiFetch as API_FETCH, controls as dataControls, select as SELECT} from '@wordpress/data-controls';

const STORAGE_KEY = 'epi_compare_products_v1';
export const COMPARE_STORE_KEY = 'epi/compare';

const read = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const arr = raw ? JSON.parse(raw) : [];
        return Array.isArray(arr) ? arr : [];
    } catch {
        return [];
    }
};

const write = (items) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {

    }
};

export const normalizeProduct = (p) => {
    if (!p) return null;
    const data = p.data || p;
    const details = (p.data?.details) || (p.details) || (p.data?.specifications) || (p.specifications) || (p.data?.features) || (p.features) || (p.data?.attributes) || (p.attributes) || (p.data?.technicalSpecs) || (p.technicalSpecs) || [];
    const images = p.images || [];
    const image =
        p.largeImage ||
        data.largeImage ||
        images?.[0]?.thumbnail ||
        images?.[0]?.href ||
        p.image ||
        p.imageUrl ||
        '';
    const name = data.name || p.productName || p.name || '';
    const sku = String(p.sku || data.sku || '');
    const salePrice = data.salePrice ?? p.salePrice;
    const regularPrice = data.regularPrice ?? p.regularPrice;

    return {
        ...p,
        sku,
        name,
        image,
        salePrice,
        regularPrice,
        details: Array.isArray(details) ? details.filter(Boolean) : [],
        href: `/product/${p.woocommerceSlug}`,
    };
};

export function deriveSharedAndAllSpecs(items) {
    const itemMaps = items.map(p => {
        const m = new Map();
        const details = Array.isArray(p.details) ? p.details : [];

        for (const d of details) {
            const name = d?.name || d?.label || d?.title || d?.displayName || d?.key;

            let val = '';

            if (Array.isArray(d?.values) && d.values.length > 0) {
                const parts = d.values
                    .map(v => String(v ?? '').trim())
                    .filter(Boolean);
                val = parts.join(', ');
            } else if (typeof d === 'string') {
                val = d;
            } else {

                val = String(d?.value ?? d?.val ?? '').trim();
            }

            const finalName = name || (typeof d === 'string' ? 'Feature' : null);
            if (!finalName || val === '') continue;

            if (!m.has(finalName)) {
                m.set(finalName, val);
            }
        }

        return m;
    });

    const allNames = new Set();
    for (const m of itemMaps) {
        for (const name of m.keys()) allNames.add(name);
    }

    const shared = [];
    const all = new Map();

    for (const name of allNames) {
        const byItem = itemMaps.map(m => String(m.get(name) ?? '').trim());
        const presentInAll =
            byItem.length === items.length && byItem.every(v => v !== '');
        const identicalInAll =
            presentInAll && byItem.every(v => v === byItem[0]);

        if (presentInAll) {
            shared.push({
                name,

                value: identicalInAll ? byItem[0] : '',
                values: byItem,
                identical: identicalInAll
            });
        }

        all.set(name, byItem);
    }

    shared.sort((a, b) => a.name.localeCompare(b.name));
    return {shared, all};
}

function deriveCompareMeta(items) {
    const {shared, all} = deriveSharedAndAllSpecs(items);

    const specNamesAll = Array.from(all.keys()).sort((a, b) => a.localeCompare(b));
    const specNamesShared = shared.map(s => s.name);

    const hasShared = specNamesShared.length > 0;

    return {
        shared,
        all,
        specNamesAll,
        specNamesShared,
        hasShared
    };
}

function buildState(items, extra = {}) {
    const meta = deriveCompareMeta(items);

    const allObj = {};
    for (const [k, v] of meta.all.entries()) {
        allObj[k] = v;
    }

    return {
        items,
        shared: meta.shared,
        all: allObj,
        specNamesAll: meta.specNamesAll,
        specNamesShared: meta.specNamesShared,
        hasShared: meta.hasShared,
        loading: extra.loading ?? false,
        error: extra.error ?? null,
    };
}

const DEFAULT_STATE = buildState(read());

const AT = {
    ADD_START: 'ADD_START',
    ADD_RECEIVE: 'ADD_RECEIVE',
    ADD_FAILED: 'ADD_FAILED',

    RELOAD_START: 'RELOAD_START',
    RELOAD_RECEIVE: 'RELOAD_RECEIVE',
    RELOAD_FAILED: 'RELOAD_FAILED',

    REMOVE: 'REMOVE',
    CLEAR: 'CLEAR',
    SET_ITEMS: 'SET_ITEMS'
};

const actions = {

    * add(productLike) {

        const skuRaw = productLike?.sku || productLike?.data?.sku;
        const sku = skuRaw != null ? String(skuRaw) : '';
        if (!sku) return;

        const already = yield SELECT(COMPARE_STORE_KEY, 'contains', sku);
        if (already) return;

        yield {type: AT.ADD_START};

        try {
            const res = yield API_FETCH({
                path: `/api/products/${encodeURIComponent(sku)}`,
                method: 'GET',
                headers: {'X-WP-Nonce': epiData?.nonce},
            });

            const full = res?.data ? res : {data: res, sku};
            const normalized = normalizeProduct(full);

            if (!normalized) {
                yield {type: AT.ADD_FAILED, err: new Error('Normalize failed')};
                return;
            }

            yield {type: AT.ADD_RECEIVE, product: normalized};
        } catch (err) {
            yield {type: AT.ADD_FAILED, err};
        }
    },

    remove(sku) {
        return {type: AT.REMOVE, sku};
    },

    clear() {
        return {type: AT.CLEAR};
    },

    setItems(items) {
        return {type: AT.SET_ITEMS, items};
    },

    * reloadAll() {
        const items = yield SELECT(COMPARE_STORE_KEY, 'getItems');
        if (!items || items.length === 0) return;

        yield {type: AT.RELOAD_START};

        try {
            const freshItems = [];
            for (const item of items) {
                try {
                    const res = yield API_FETCH({
                        path: `/api/products/${encodeURIComponent(item.sku)}`,
                        method: 'GET',
                        headers: {'X-WP-Nonce': epiData?.nonce},
                    });

                    const full = res?.data ? res : {data: res, sku: item.sku, source: item.source};
                    const normalized = normalizeProduct(full);

                    if (normalized) {
                        freshItems.push(normalized);
                    } else {
                        freshItems.push(item);
                    }
                } catch (e) {
                    freshItems.push(item);
                }
            }
            yield {type: AT.RELOAD_RECEIVE, items: freshItems};
        } catch (err) {
            yield {type: AT.RELOAD_FAILED, err};
        }
    },
};

function reducer(state = DEFAULT_STATE, action) {
    switch (action.type) {
        case AT.ADD_START:
            return {...state, loading: true, error: null};

        case AT.ADD_RECEIVE: {
            const n = action.product;
            if (!n || !n.sku) return {...state, loading: false};

            const exists = state.items.some(i => String(i.sku) === String(n.sku));
            if (exists) return {...state, loading: false};

            const nextItems = [...state.items, n].slice(-4);
            return buildState(nextItems, {loading: false, error: null});
        }

        case AT.ADD_FAILED:
            return {...state, loading: false, error: action.err || true};

        case AT.RELOAD_START:
            return {...state, loading: true, error: null};

        case AT.RELOAD_RECEIVE:
            return buildState(action.items, {loading: false, error: null});

        case AT.RELOAD_FAILED:
            return {...state, loading: false, error: action.err || true};

        case AT.REMOVE: {
            const nextItems = state.items.filter(i => String(i.sku) !== String(action.sku));
            return buildState(nextItems);
        }

        case AT.CLEAR:
            return buildState([]);

        case AT.SET_ITEMS: {
            const incoming = Array.isArray(action.items) ? action.items : [];
            return buildState(incoming);
        }

        default:
            return state;
    }
}

const selectors = {
    getItems(state) {
        return state.items;
    },
    getShared(state) {
        return state.shared;
    },

    getAll(state) {
        return state.all;
    },
    getMeta(state) {
        const allMap = new Map(Object.entries(state.all || {}));
        return {
            shared: state.shared,
            all: allMap,
            specNamesAll: state.specNamesAll,
            specNamesShared: state.specNamesShared,
            sharedSet: new Set(state.specNamesShared),
            hasShared: state.hasShared
        };
    },
    contains(state, sku) {
        return state.items.some(i => String(i.sku) === String(sku));
    },
    isLoading(state) {
        return state.loading;
    },
    getError(state) {
        return state.error;
    }
};

const controls = {
    ...dataControls,
};

export const store = createReduxStore(COMPARE_STORE_KEY, {
    reducer,
    actions,
    selectors,
    controls,
});

register(store);

const GLOBAL_GUARD_KEY = '__epi_compare_store_persistence_v4__';

if (typeof window !== 'undefined' && !window[GLOBAL_GUARD_KEY]) {
    window[GLOBAL_GUARD_KEY] = true;

    let lastItemsJSON = JSON.stringify(select(COMPARE_STORE_KEY).getItems());

    subscribe(() => {
        const items = select(COMPARE_STORE_KEY).getItems();
        const json = JSON.stringify(items);
        if (json !== lastItemsJSON) {
            lastItemsJSON = json;
            write(items);
        }
    });

    window.addEventListener('storage', (e) => {
        if (e.key !== STORAGE_KEY) return;
        const incoming = read();
        const current = select(COMPARE_STORE_KEY).getItems();

        if (JSON.stringify(incoming) !== JSON.stringify(current)) {
            dispatch(COMPARE_STORE_KEY).setItems(incoming);
        }
    });
}

export const useCompareDispatch = () => useDispatch(COMPARE_STORE_KEY);

export function useCompareItems() {
    return useSelect(
        (sel) => sel(COMPARE_STORE_KEY).getItems(),
        []
    );
}

export function useCompareMeta() {
    return useSelect(
        (sel) => sel(COMPARE_STORE_KEY).getMeta(),
        []
    );
}

export function useCompareStatus() {
    return useSelect(
        (sel) => ({
            loading: sel(COMPARE_STORE_KEY).isLoading(),
            error: sel(COMPARE_STORE_KEY).getError(),
        }),
        []
    );
}

export function useCompare() {
    const items = useCompareItems();
    const meta = useCompareMeta();
    const status = useCompareStatus();
    const {add, remove, clear, reloadAll} = useCompareDispatch();

    const contains = useSelect(
        (sel) => (sku) => sel(COMPARE_STORE_KEY).contains(sku),
        []
    );

    return {
        items,
        add,
        remove,
        clear,
        reloadAll,
        contains,
        ...meta,
        ...status
    };
}